import type { TextFieldProps } from '@mui/material/TextField'
import type { SxProps, Theme } from '@mui/material/styles'

export const OHIO_TITLES_DOCUMENT_DOWNLOAD_LABEL = 'DOWNLOAD'

const ohioTitlesTableBodyFontSize = '9pt'
const ohioTitlesTableHeaderFontSize = '0.8rem'
const ohioTitlesControlMinHeight = 28

/** Plain style objects for TextField/Button `sx` (avoids SxProps array typing issues). */
export const ohioTitlesCompactTextFieldSx = {
  '& .MuiInputBase-root': {
    fontSize: ohioTitlesTableBodyFontSize
  },
  '& .MuiOutlinedInput-root': {
    minHeight: ohioTitlesControlMinHeight
  },
  '& .MuiOutlinedInput-input': {
    fontSize: ohioTitlesTableBodyFontSize,
    py: 0.375,
    px: 0.75
  },
  '& .MuiSelect-select': {
    fontSize: ohioTitlesTableBodyFontSize,
    py: 0.375,
    minHeight: 'unset !important'
  },
  '& .MuiInputLabel-root': {
    fontSize: ohioTitlesTableHeaderFontSize
  },
  '& .MuiFormHelperText-root': {
    fontSize: '0.7rem',
    lineHeight: 1.2,
    m: 0,
    mt: 0.25
  }
}

export const ohioTitlesCompactButtonSx = {
  fontSize: ohioTitlesTableHeaderFontSize,
  lineHeight: 1.25,
  py: 0.375,
  px: 1.25,
  minHeight: ohioTitlesControlMinHeight,
  textTransform: 'none'
}

/** Address/party table: middle-align all rows (text-only and select rows). */
export const ohioTitlesPartyTableSx: SxProps<Theme> = {
  '& .MuiTableCell-body': {
    verticalAlign: 'middle'
  }
}

export const ohioTitlesPartyActionsCellSx: SxProps<Theme> = {
  verticalAlign: 'middle',
  whiteSpace: 'nowrap'
}

export const ohioTitlesCompactFormControlLabelSx = {
  m: 0,
  alignItems: 'center',
  gap: 0.5,
  '& .MuiFormControlLabel-label': {
    fontSize: ohioTitlesTableBodyFontSize,
    lineHeight: 1.35
  },
  '& .MuiCheckbox-root': {
    padding: '2px'
  }
}

export const ohioTitlesCompactSelectProps: TextFieldProps['SelectProps'] = {
  MenuProps: {
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root': {
          fontSize: ohioTitlesTableBodyFontSize,
          py: 0.5,
          minHeight: ohioTitlesControlMinHeight
        }
      }
    }
  }
}

export const ohioTitlesTableContainerSx: SxProps<Theme> = {
  overflowX: 'auto'
}

export const ohioTitlesTableSx: SxProps<Theme> = theme => ({
  tableLayout: 'auto',
  '& .MuiTableCell-head': {
    color: theme.palette.primary.main,
    fontSize: ohioTitlesTableHeaderFontSize,
    fontWeight: 600,
    lineHeight: 1.25,
    py: 0.75,
    px: 0.75,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    verticalAlign: 'bottom'
  },
  '& .MuiTableSortLabel-root': {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    alignItems: 'flex-start',
    color: 'inherit'
  },
  '& .MuiTableSortLabel-icon': {
    fontSize: '1rem'
  },
  '& .MuiTableCell-body': {
    fontSize: ohioTitlesTableBodyFontSize,
    lineHeight: 1.35,
    py: 0.5,
    px: 0.75,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    verticalAlign: 'top'
  },
  '& .MuiTableRow-root:has(.MuiFormControl-root) .MuiTableCell-body': {
    verticalAlign: 'middle'
  },
  '& .MuiTableCell-body .MuiLink-root': {
    fontSize: 'inherit',
    lineHeight: 'inherit',
    whiteSpace: 'nowrap'
  },
  '& .MuiTableCell-body .MuiButton-root': {
    fontSize: ohioTitlesTableHeaderFontSize,
    lineHeight: 1.25,
    minWidth: 0,
    py: 0.375,
    px: 0.75,
    minHeight: ohioTitlesControlMinHeight
  },
  '& .MuiTableCell-body .MuiFormControl-root': {
    my: 0,
    justifyContent: 'center'
  },
  '& .MuiTableCell-body .MuiOutlinedInput-root': {
    minHeight: ohioTitlesControlMinHeight
  },
  '& .MuiTableCell-body .MuiInputBase-input': {
    fontSize: ohioTitlesTableBodyFontSize,
    py: 0.375,
    px: 0.75
  },
  '& .MuiTableCell-body .MuiSelect-select': {
    fontSize: ohioTitlesTableBodyFontSize,
    py: 0.375,
    minHeight: 'unset !important'
  },
  '& .MuiTableCell-body .MuiFormHelperText-root': {
    fontSize: '0.7rem',
    lineHeight: 1.2,
    m: 0,
    mt: 0.25
  },
  '& .MuiTableCell-paddingCheckbox': {
    py: 0.25,
    px: 0.5,
    width: 40,
    verticalAlign: 'middle'
  },
  '& .MuiTablePagination-root': {
    fontSize: ohioTitlesTableBodyFontSize
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
    fontSize: ohioTitlesTableBodyFontSize
  },
  '& .MuiTablePagination-toolbar': {
    minHeight: 40
  }
})

/** Base table styles plus party-table body middle alignment (single `sx` for MUI typing). */
export const ohioTitlesPartyTableMergedSx: SxProps<Theme> = theme => {
  const tableStyles = ohioTitlesTableSx(theme) as Record<string, Record<string, unknown> | unknown>
  const bodyCellStyles = tableStyles['& .MuiTableCell-body'] as Record<string, unknown> | undefined

  return {
    ...tableStyles,
    '& .MuiTableCell-body': {
      ...bodyCellStyles,
      verticalAlign: 'middle'
    }
  }
}

export const ohioTitlesDateCellSx: SxProps<Theme> = {
  whiteSpace: 'nowrap'
}

export const ohioTitlesCompactCellSx: SxProps<Theme> = {
  whiteSpace: 'nowrap'
}

export const ohioTitlesDocumentCellSx: SxProps<Theme> = {
  whiteSpace: 'nowrap'
}

export const ohioTitlesMissingCellSx: SxProps<Theme> = theme => ({
  color: theme.palette.error.main
})

export const ohioTitlesRowLabelCellSx: SxProps<Theme> = theme => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  verticalAlign: 'middle'
})

export const ohioTitlesLinkSx: SxProps<Theme> = {
  color: 'inherit',
  cursor: 'pointer',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  whiteSpace: 'nowrap'
}
